import { useState } from "react";
import { Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { TenantLayout, useTenant } from "@/components/tenant/tenant-layout";
import { listDocuments, type DocumentListRow, type DocumentStatus } from "@/lib/api/documents";
import { listFunds } from "@/lib/api/funds";
import { listLps } from "@/lib/api/lps";
import { useRole } from "@/lib/auth/use-role";

const STATUS_STYLES: Record<DocumentStatus, string> = {
  pending: "bg-neutral-800 text-neutral-300",
  parsing: "bg-sky-950 text-sky-300",
  extracting: "bg-sky-950 text-sky-300",
  extracted: "bg-emerald-950 text-emerald-300",
  failed: "bg-red-950 text-red-300",
  archived: "bg-neutral-800 text-neutral-500",
};

const TYPE_LABELS: Record<DocumentListRow["document_type"], string> = {
  side_letter: "Side letter",
  lpa: "LPA",
  sub_doc: "Sub doc",
  amendment: "Amendment",
  other: "Other",
};

function DocumentsListInner() {
  const tenant = useTenant();
  const { canWrite, role } = useRole(tenant.id);
  const canUpload = canWrite || role === "reviewer";
  const [fundFilter, setFundFilter] = useState<string>("");

  const { data: funds } = useQuery({
    queryKey: ["funds", tenant.id],
    queryFn: () => listFunds(tenant.id),
  });
  const { data: lps } = useQuery({
    queryKey: ["lps", tenant.id],
    queryFn: () => listLps(tenant.id),
  });
  const {
    data: documents,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["documents", tenant.id, fundFilter],
    queryFn: () => listDocuments(tenant.id, fundFilter ? { fundId: fundFilter } : {}),
  });

  const fundName = (id: string): string => funds?.find((f) => f.id === id)?.name ?? "—";
  const lpName = (id: string | null): string =>
    id === null ? "Fund-level" : (lps?.find((l) => l.id === id)?.legal_name ?? "—");

  return (
    <>
      <header className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight">Documents</h1>
          <p className="text-sm text-neutral-400">
            Side letters, LPAs, and amendments — uploaded, parsed, and extracted.
          </p>
        </div>
        {canUpload ? (
          <Link
            to="/t/$slug/documents/upload"
            params={{ slug: tenant.slug }}
            className="rounded bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-950 hover:bg-white"
          >
            Upload document
          </Link>
        ) : null}
      </header>

      <div className="flex items-center gap-2 text-xs">
        <label htmlFor="fund-filter" className="text-neutral-500">
          Fund
        </label>
        <select
          id="fund-filter"
          value={fundFilter}
          onChange={(e) => setFundFilter(e.target.value)}
          className="rounded border border-neutral-800 bg-neutral-900 px-2 py-1 text-neutral-200"
        >
          <option value="">All funds</option>
          {funds?.map((f) => (
            <option key={f.id} value={f.id}>
              {f.name}
            </option>
          ))}
        </select>
      </div>

      {isLoading ? (
        <p className="text-sm text-neutral-500">Loading documents…</p>
      ) : error ? (
        <div className="rounded border border-red-900/60 bg-red-950/30 px-3 py-2 text-xs text-red-300">
          {error.message}
        </div>
      ) : !documents || documents.length === 0 ? (
        <div className="rounded border border-neutral-800 bg-neutral-900 p-6 text-center text-sm text-neutral-400">
          <p className="font-medium text-neutral-100">No documents yet</p>
          <p className="mt-1">
            {canUpload
              ? "Upload your first side letter to see the extraction engine work."
              : "No documents have been uploaded to this tenant yet."}
          </p>
          {canUpload ? (
            <Link
              to="/t/$slug/documents/upload"
              params={{ slug: tenant.slug }}
              className="mt-3 inline-block rounded bg-neutral-100 px-3 py-1.5 text-xs font-medium text-neutral-950 hover:bg-white"
            >
              Upload document
            </Link>
          ) : null}
        </div>
      ) : (
        <div className="overflow-hidden rounded border border-neutral-800">
          <table className="w-full text-left text-sm">
            <thead className="border-b border-neutral-800 bg-neutral-900/50 text-[11px] uppercase tracking-wide text-neutral-500">
              <tr>
                <th className="px-3 py-2">Title</th>
                <th className="px-3 py-2">Type</th>
                <th className="px-3 py-2">Fund</th>
                <th className="px-3 py-2">LP</th>
                <th className="px-3 py-2">Pages</th>
                <th className="px-3 py-2">Status</th>
                <th className="px-3 py-2">Uploaded</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-neutral-900">
              {documents.map((d) => (
                <tr key={d.id} className="hover:bg-neutral-900/50">
                  <td className="px-3 py-2.5">
                    <Link
                      to="/t/$slug/documents/$documentId"
                      params={{ slug: tenant.slug, documentId: d.id }}
                      className="text-neutral-100 hover:underline"
                    >
                      {d.title}
                    </Link>
                  </td>
                  <td className="px-3 py-2.5 text-neutral-400">{TYPE_LABELS[d.document_type]}</td>
                  <td className="px-3 py-2.5 text-neutral-400">{fundName(d.fund_id)}</td>
                  <td className="px-3 py-2.5 text-neutral-400">{lpName(d.lp_id)}</td>
                  <td className="px-3 py-2.5 text-neutral-400">{d.page_count ?? "—"}</td>
                  <td className="px-3 py-2.5">
                    <span
                      className={`inline-block rounded px-1.5 py-0.5 text-[11px] font-medium ${STATUS_STYLES[d.status]}`}
                    >
                      {d.status}
                    </span>
                  </td>
                  <td className="px-3 py-2.5 text-neutral-500">
                    {new Date(d.created_at).toLocaleDateString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </>
  );
}

export function DocumentsListPage() {
  return (
    <TenantLayout>
      <DocumentsListInner />
    </TenantLayout>
  );
}
